import React from 'react';
import { Scan, AlertTriangle } from 'lucide-react';
import type { FaceRecognitionSection } from '../../api/insights';
import shared from './shared.module.css';

interface Props {
  data: FaceRecognitionSection;
}

export function FaceRecognitionGroup({ data }: Props) {
  const lowMatchRate = data.eventsEnabled > 0 && data.totalSearches > 0 && data.avgMatchesPerSearch < 3;

  if (data.eventsEnabled === 0) {
    return (
      <div className={shared['group-card']}>
        <div className={shared['group-header']}>
          <div className={shared['group-icon']}><Scan size={15} /></div>
          <div>
            <div className={shared['group-title']}>Face Recognition</div>
            <div className={shared['group-subtitle']}>Face search and Google Photos sync performance</div>
          </div>
        </div>
        <span className={shared['sub-empty']}>
          Enable face recognition on an event to see stats here.
        </span>
      </div>
    );
  }

  return (
    <div className={shared['group-card']}>
      <div className={shared['group-header']}>
        <div className={shared['group-icon']}><Scan size={15} /></div>
        <div>
          <div className={shared['group-title']}>Face Recognition</div>
          <div className={shared['group-subtitle']}>Face search and Google Photos sync performance</div>
        </div>
      </div>

      <div className={shared['stat-row']}>
        <div className={shared['stat-card']}>
          <span className={shared['stat-label']}>Events enabled</span>
          <span className={shared['stat-value']}>{data.eventsEnabled} / {data.totalEventsCreated}</span>
        </div>
        <div className={shared['stat-card']}>
          <span className={shared['stat-label']}>Face searches</span>
          <span className={shared['stat-value']}>{data.totalSearches.toLocaleString()}</span>
        </div>
        <div className={shared['stat-card']}>
          <span className={shared['stat-label']}>Avg matches / search</span>
          <span className={`${shared['stat-value']} ${lowMatchRate ? shared['stat-value--amber'] : ''}`}>
            {data.avgMatchesPerSearch}
          </span>
        </div>
        <div className={shared['stat-card']}>
          <span className={shared['stat-label']}>Guests with matches</span>
          <span className={shared['stat-value']}>{data.guestsWithMatches.toLocaleString()}</span>
        </div>
      </div>

      {lowMatchRate && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
          padding: '0.75rem', borderRadius: 'var(--radius-clay-sm)',
          background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)',
        }}>
          <AlertTriangle size={14} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 1 }} />
          <span style={{ fontSize: '0.8125rem', color: '#f59e0b' }}>
            Low match rate — check that face indexing completed and guests have uploaded a face photo.
          </span>
        </div>
      )}

      {/* Google Photos */}
      <div>
        <div className={shared['section-label']}>Google Photos sync</div>
        <div className={shared['stat-row']}>
          <div className={shared['stat-card']}>
            <span className={shared['stat-label']}>Photos synced</span>
            <span className={shared['stat-value']}>{data.googlePhotos.photosSynced.toLocaleString()}</span>
          </div>
          <div className={shared['stat-card']}>
            <span className={shared['stat-label']}>Sync success rate</span>
            <span className={`${shared['stat-value']} ${data.googlePhotos.syncJobSuccessRate >= 95 ? shared['stat-value--green'] : shared['stat-value--amber']}`}>
              {data.googlePhotos.syncJobSuccessRate}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
